// HTTP transport of the uploader: JSON calls through the generated client (a 404 source file means "not stored yet"; a 404 or 410 session means "start again"), and each part, under the admin uploads or another upload route, as an XMLHttpRequest PUT of application/octet-stream with its Content-Digest and traceparent that sends the session cookie like the API client (with credentials when the API is another origin) and reports a 401 unauthenticated to its signed-out handler, so an ended session leads to sign-in, the upload reports progress within a part and can be aborted.
import {
  uploadsCompleteUpload,
  uploadsCreateUpload,
  uploadsDeleteUpload,
  uploadsGetSourceFile,
  uploadsGetUpload,
} from "@/api/__generated__";
import { apiHeaders, apiUrl, reportSignedOutBody, sendsCredentialsCrossOrigin } from "@/api/client";
import { ApiProblem } from "@/api/problem";

import type { PartSender, UploadTransport } from "./uploader";

const GONE = new Set([404, 410]);

export function uploadStatus(problem: unknown): number | null {
  return problem instanceof ApiProblem ? problem.status : null;
}

export function isGoneUpload(problem: unknown): boolean {
  const code = uploadStatus(problem);
  return code !== null && GONE.has(code);
}

export const partSender = (uploadsPath: string): PartSender =>
  function putPart(uploadId, partNumber, data, digest, onProgress, signal) {
    const headers = apiHeaders({ "Content-Type": "application/octet-stream", "Content-Digest": digest });
    return new Promise((resolve, reject) => {
      const request = new XMLHttpRequest();
      request.open(
        "PUT",
        apiUrl(`${uploadsPath}/${encodeURIComponent(uploadId)}/parts/${String(partNumber)}`),
      );
      request.withCredentials = sendsCredentialsCrossOrigin();
      headers.forEach((value, name) => {
        request.setRequestHeader(name, value);
      });
      request.responseType = "json";
      request.upload.addEventListener("progress", (event) => {
        onProgress(event.loaded);
      });
      request.addEventListener("load", () => {
        if (request.status >= 200 && request.status < 300) {
          resolve(request.response as { part_number: number; size_bytes: number; sha256: string });
        } else {
          reportSignedOutBody(request.status, request.response);
          reject(ApiProblem.fromBody(request.status, request.response));
        }
      });
      request.addEventListener("error", () => {
        reject(ApiProblem.network(new Error("The part could not be sent.")));
      });
      request.addEventListener("abort", () => {
        reject(new DOMException("Paused.", "AbortError"));
      });
      signal.addEventListener("abort", () => {
        request.abort();
      });
      request.send(data);
    });
  };

export const httpUploadTransport: UploadTransport = {
  async findSourceFile(sha256) {
    try {
      const { data } = await uploadsGetSourceFile({ path: { sha256 }, throwOnError: true });
      return data;
    } catch (problem) {
      if (uploadStatus(problem) === 404) {
        return null;
      }
      throw problem;
    }
  },
  async createUpload(body) {
    const { data } = await uploadsCreateUpload({ body, throwOnError: true });
    return data;
  },
  async getUpload(uploadId) {
    try {
      const { data } = await uploadsGetUpload({ path: { upload_id: uploadId }, throwOnError: true });
      return data;
    } catch (problem) {
      if (isGoneUpload(problem)) {
        return null;
      }
      throw problem;
    }
  },
  putPart: partSender("/api/v1/admin/uploads"),
  async completeUpload(uploadId, profile) {
    const { data } = await uploadsCompleteUpload({
      path: { upload_id: uploadId },
      body: { profile },
      throwOnError: true,
    });
    return data;
  },
  async deleteUpload(uploadId) {
    await uploadsDeleteUpload({ path: { upload_id: uploadId }, throwOnError: true });
  },
};
